import styled from '@emotion/styled';
import { Card, rem } from '@triumph/design-system';

const CARD_BORDER = (theme: any) =>
  `${rem(1)} solid ${theme.colors.greyscale.gray15}`;

/** Shared panel: design-system Card with the rounded, flat, bordered treatment. */
const PanelCard = styled(Card)`
  border: ${({ theme }) => CARD_BORDER(theme)};
  border-radius: ${rem(12)};
  min-height: 0;
`;

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${rem(8)};
  background: none;
  border: none;
  padding: 0 0 ${rem(16)} 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};

  &:hover {
    color: ${({ theme }) => theme.colors.greyscale.gray50};
  }
`;

export const PageDescription = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(16)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
  padding-bottom: ${rem(24)};
  max-width: ${rem(640)};
`;

/* ── Search ────────────────────────────────────────────────────────────── */

export const SearchSection = styled(PanelCard)`
  padding: ${rem(20)} ${rem(24)};
  margin-bottom: ${rem(16)};
`;

export const SearchResultCount = styled.div`
  padding-top: ${rem(12)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

/* ── Two-column body ───────────────────────────────────────────────────── */

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(16)};
  align-items: flex-start;
  width: 100%;
`;

export const ResultsPanel = styled(PanelCard)`
  flex: 1;
  min-width: 0;
  max-width: calc(100% - ${rem(372)} - ${rem(16)});
`;

export const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(20)} ${rem(24)};
  border-bottom: ${({ theme }) => CARD_BORDER(theme)};
`;

export const ResultsTitle = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(18)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const ResultsCount = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

/**
 * Styles PrimeTable as a borderless, header-less card list: hides the column
 * header, removes zebra striping, and highlights the selected row to match the
 * prototype. PrimeReact's base table CSS ships with @triumph/design-system.
 */
export const CarrierTableWrapper = styled.div`
  max-height: ${rem(560)};
  overflow-y: auto;

  .p-datatable-thead {
    display: none;
  }

  .p-datatable .p-datatable-tbody > tr {
    background: ${({ theme }) => theme.colors.greyscale.white};
    cursor: pointer;
    transition: background 0.1s ease-in-out;
  }

  .p-datatable .p-datatable-tbody > tr > td {
    border-top: ${({ theme }) => `${rem(1)} solid ${theme.colors.greyscale.gray15}`};
    padding: ${rem(12)} ${rem(24)};
  }

  .p-datatable .p-datatable-tbody > tr:first-of-type > td {
    border-top: none;
  }

  .p-datatable .p-datatable-tbody > tr:hover {
    background: ${({ theme }) => theme.colors.greyscale.gray2_5};
  }

  .p-datatable .p-datatable-tbody > tr.p-highlight,
  .p-datatable .p-datatable-tbody > tr.carrier-row--selected {
    background: ${({ theme }) =>
      theme.colors.secondary?.actionHoverSecondary ?? '#eef4ff'};
  }
`;

/* ── Carrier row content ───────────────────────────────────────────────── */

export const CarrierRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  width: 100%;
`;

/** The rounded icon tile shown at the start of a carrier row. */
export const CarrierIconBox = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: ${rem(8)};
  background: ${({ selected, theme }) =>
    selected ? theme.colors.greyscale.gray50 : theme.colors.greyscale.gray5};
`;

export const CarrierRowInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
  flex: 1;
`;

export const CarrierRowName = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(14)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const CarrierRowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(12)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const CarrierRowTrailing = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-shrink: 0;
`;

/* ── Detail panel ──────────────────────────────────────────────────────── */

export const DetailPanel = styled(PanelCard)`
  width: ${rem(372)};
  min-width: ${rem(372)};
  padding: ${rem(24)};
`;

export const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`;

export const DetailCarrierName = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(18)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const DetailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(12)};
  padding-top: ${rem(6)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const DetailSectionLabel = styled.div`
  margin-top: ${rem(24)};
  padding-bottom: ${rem(12)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(11)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  letter-spacing: ${rem(0.8)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

/* ── NOA document card ─────────────────────────────────────────────────── */

export const DocumentCard = styled.div<{ highlight?: boolean }>`
  border: ${rem(1)} solid
    ${({ highlight, theme }) =>
      highlight ? theme.colors.semantic.successDefault : theme.colors.greyscale.gray15};
  border-radius: ${rem(8)};
  padding: ${rem(16)};
`;

export const DocumentCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
`;

export const DocumentTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex: 1;
  min-width: 0;
`;

export const DocumentBadgeRow = styled.div`
  display: flex;
  gap: ${rem(6)};
  flex-wrap: wrap;
`;

export const DocumentName = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(14)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const DocumentVersion = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const DocumentMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${rem(12)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
`;

export const DocumentMetaLabel = styled.span`
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const DocumentMetaValue = styled.span`
  color: ${({ theme }) => theme.colors.greyscale.gray50};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

export const DocumentActions = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding-top: ${rem(16)};

  & > * {
    flex: 1;
  }
`;

export const DocumentUnavailableNote = styled.div`
  padding-top: ${rem(8)};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const StateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(320)};
  padding: ${rem(24)};
`;

/* ── No-document state (bordered tile inside the detail panel) ──────────── */

export const NoDocumentState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${rem(8)};
  padding: ${rem(40)} ${rem(24)};
  border: ${rem(1)} solid ${({ theme }) => theme.colors.greyscale.gray15};
  border-radius: ${rem(8)};
`;

export const NoDocumentIconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  margin-bottom: ${rem(8)};
  border-radius: ${rem(10)};
  background: ${({ theme }) => theme.colors.greyscale.gray5};
`;

export const NoDocumentTitle = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(15)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const NoDocumentText = styled.div`
  max-width: ${rem(260)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
  line-height: ${({ theme }) => theme.typography.lineHeights.body.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${rem(320)};
  padding: ${rem(24)};
  gap: ${rem(8)};
`;

export const EmptyStateIconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  margin-bottom: ${rem(8)};
  border-radius: ${rem(10)};
  background: ${({ theme }) => theme.colors.greyscale.gray5};
`;

export const EmptyStateTitle = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(16)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const EmptyStateText = styled.div`
  max-width: ${rem(260)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
  line-height: ${({ theme }) => theme.typography.lineHeights.body.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;
